import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Timer, Users, PaperPlaneTilt, ShoppingBag, Plus, Minus, Sparkle } from "@phosphor-icons/react";
import ChatWidget from "../components/ChatWidget";
import SmartAddFlow from "../components/SmartAddFlow";
import WaitWatchFlow from "../components/WaitWatchFlow";
import CallbackFlow from "../components/CallbackFlow";
import GuestPrivacyCard from "../components/GuestPrivacyCard";

export default function PublicJoin() {
  const { slug } = useParams();
  const [biz, setBiz] = useState(null);
  const [menu, setMenu] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", party_size: 2, notes: "", source: "web" });
  const [order, setOrder] = useState({ customer_name: "", phone: "", notes: "" });
  const [cart, setCart] = useState({});
  const [joined, setJoined] = useState(null);
  const [orderPlaced, setOrderPlaced] = useState(null);
  const [history, setHistory] = useState(null);
  const [waitStatus, setWaitStatus] = useState(null);
  const debounceRef = useRef();

  useEffect(() => {
    api.get(`/public/business/${slug}`).then((r) => setBiz(r.data)).catch(() => setBiz(false));
    api.get(`/public/menu/${slug}`).then((r) => setMenu(r.data)).catch(() => {});
    const loadStatus = () => api.get(`/public/wait-status/${slug}`).then((r) => setWaitStatus(r.data)).catch(() => {});
    loadStatus();
    const t = setInterval(loadStatus, 30000); // refresh every 30s
    return () => clearInterval(t);
  }, [slug]);

  // Look up returning-guest history when phone reaches 10+ digits
  // Live wait quote when party size changes
  const [waitQuote, setWaitQuote] = useState(null);
  useEffect(() => {
    if (!form.party_size || form.party_size < 1) { setWaitQuote(null); return; }
    api.get(`/public/wait-quote/${slug}?party_size=${form.party_size}`).then((r) => setWaitQuote(r.data)).catch(() => setWaitQuote(null));
  }, [form.party_size, slug]);

  useEffect(() => {
    if (!form.phone || form.phone.replace(/\D/g, "").length < 7) { setHistory(null); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.get(`/public/guest-history/${slug}?phone=${encodeURIComponent(form.phone)}`)
        .then((r) => {
          if (r.data.is_returning) {
            setHistory(r.data);
            // Auto-fill name and party size if empty
            setForm((f) => ({
              ...f,
              name: f.name || r.data.last_name || "",
              party_size: f.party_size === 2 && r.data.last_party_size ? r.data.last_party_size : f.party_size,
            }));
          } else {
            setHistory(null);
          }
        }).catch(() => setHistory(null));
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [form.phone, slug]);

  if (biz === false) return <div className="min-h-screen flex items-center justify-center text-[#5C6B61]">Business not found.</div>;
  if (!biz) return <div className="min-h-screen flex items-center justify-center text-[#5C6B61]">Loading…</div>;

  const joinList = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(`/public/join/${slug}`, form);
      if (r.data.standby) { setJoined({ ...r.data, standby: true }); toast.success("You're on the standby list — we'll text if a spot opens up."); }
      else { setJoined(r.data); toast.success("You're on the list! We'll text you."); }
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Backend returns a structured payload when waitlist is full but standby is available
      if (detail && typeof detail === "object" && detail.full && detail.standby_available) {
        const ok = window.confirm(`${detail.reason || "The waitlist is full right now."}\n\nWould you like to join the standby list? We'll text you the moment a table opens up.`);
        if (!ok) return;
        try {
          const r2 = await api.post(`/public/join/${slug}`, { ...form, standby_ok: true });
          setJoined({ ...r2.data, standby: true });
          toast.success("You're on the standby list — we'll text if a spot opens up.");
        } catch (err2) {
          toast.error(err2.response?.data?.detail || "Failed to join standby");
        }
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to join");
      }
    }
  };

  const total = Object.entries(cart).reduce((s, [id, qty]) => {
    const m = menu.find((x) => x.id === id); return s + (m ? m.price * qty : 0);
  }, 0);

  const placeOrder = async (e) => {
    e.preventDefault();
    const items = Object.entries(cart).filter(([, q]) => q > 0).map(([id, qty]) => {
      const m = menu.find((x) => x.id === id); return { id, name: m.name, qty };
    });
    if (items.length === 0) return toast.error("Add at least one item.");
    try { const r = await api.post(`/public/orders/${slug}`, { ...order, items }); setOrderPlaced(r.data); toast.success("Order placed!"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to order"); }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#1E3A2F] flex items-center justify-center">
              <PaperPlaneTilt size={18} color="#fff" weight="fill" />
            </div>
            <span className="font-heading font-extrabold">BuzzerAI</span>
          </Link>
          <span className="text-sm text-[#5C6B61]">powered by BuzzerAI</span>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 pt-10 pb-24">
        <div className="overline mb-2">You're joining</div>
        <h1 className="text-4xl mb-1">{biz.business_name}</h1>
        <p className="text-[#5C6B61]">{biz.address || "Welcome. We're glad to have you."}</p>

        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="paper-card p-4 flex items-center gap-3">
            <Users size={22} color="#1E3A2F" />
            <div><div className="text-2xl font-heading font-extrabold">{biz.current_wait_count}</div><div className="text-xs text-[#5C6B61]">parties ahead</div></div>
          </div>
          <div className="paper-card p-4 flex items-center gap-3">
            <Timer size={22} color="#D96C5B" />
            <div><div className="text-2xl font-heading font-extrabold">~{biz.estimated_wait}m</div><div className="text-xs text-[#5C6B61]">est. wait time</div></div>
          </div>
        </div>

        <Tabs defaultValue="waitlist" className="mt-8">
          <TabsList className="bg-white border border-[#E1DFD5]">
            <TabsTrigger value="waitlist" data-testid="tab-waitlist">Join waitlist</TabsTrigger>
            <TabsTrigger value="smartadd" data-testid="tab-smartadd">Wait Watch</TabsTrigger>
            {/* To-Go tab removed — routed to live person via AI receptionist until in-app to-go ships (see Coming Soon). */}
          </TabsList>

          <TabsContent value="waitlist">
            {joined ? (
              <div className="paper-card p-8 mt-4 text-center">
                <div className="w-14 h-14 rounded-full bg-[#C2D1C1] mx-auto flex items-center justify-center mb-3">
                  <Users size={24} color="#1E3A2F" />
                </div>
                <h2 className="text-2xl mb-2">You're on the list, {joined.name}!</h2>
                <p className="text-[#5C6B61]">We'll text {joined.phone} when your spot is ready.</p>
              </div>
            ) : (
              <form onSubmit={joinList} className="paper-card p-6 mt-4 space-y-4">
                {waitStatus?.full && (
                  <div className="paper-card p-3 border border-[#A5382A]/30 bg-[#F1D7D2]/50" data-testid="waitlist-full-banner">
                    <div className="overline text-[#A5382A] mb-1">Waitlist is full right now</div>
                    <p className="text-xs text-[#7A5A00] leading-snug">{waitStatus.reason || "We've hit capacity for tonight."}
                      {waitStatus.standby_enabled ? " Enter your info and we'll add you to the standby list — we'll text the moment a spot opens up." : " Try again a bit later."}
                    </p>
                  </div>
                )}
                {history && history.is_returning && (
                  <div className="paper-card p-4 bg-[#C2D1C1]/40 border-[#1E3A2F]/20 flex items-start gap-3 -mb-1">
                    <Sparkle size={22} color="#D96C5B" weight="fill" />
                    <div className="flex-1">
                      <div className="font-heading font-extrabold">Welcome back, {history.last_name}!</div>
                      <div className="text-xs text-[#5C6B61] mt-1">
                        Visit #{history.visits + 1} · usual party of {history.last_party_size || "?"}
                        {history.favorite_items && history.favorite_items.length > 0 && <> · often orders {history.favorite_items.slice(0, 2).join(", ")}</>}
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <label className="overline block mb-1">Mobile phone</label>
                  <input data-testid="join-phone" className="input-field" placeholder="+15555550100" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  <p className="text-[11px] text-[#5C6B61] mt-1 leading-snug">By providing your number you consent to receive text messages about your spot. Msg &amp; data rates may apply. Reply <b>STOP</b> to opt out at any time.</p>
                </div>
                <div>
                  <label className="overline block mb-1">Name</label>
                  <input data-testid="join-name" className="input-field" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label className="overline block mb-1">Party size <span className="text-[10px] text-[#5C6B61]">(1–8 · larger parties call the restaurant)</span></label>
                  <input data-testid="join-party" className="input-field" type="number" min={1} max={8} required value={form.party_size} onChange={(e) => setForm({ ...form, party_size: parseInt(e.target.value || "1") })} />
                  {form.party_size > 8 && (
                    <p className="text-xs mt-1 text-[#A5382A] leading-snug" data-testid="join-party-toobig">
                      For parties larger than 8, please call the restaurant directly so a manager can arrange it.
                    </p>
                  )}
                  {waitQuote && waitQuote.minutes != null && (
                    <p className="text-xs mt-1 leading-snug" data-testid="wait-quote">
                      <b className="text-[#1E3A2F]">Estimated wait: ~{waitQuote.minutes} min</b>
                      <span className="text-[#5C6B61]"> {waitQuote.method === "table_aware" ? ` · based on ${waitQuote.parties_ahead || 0} parties ahead and ${waitQuote.compatible_tables || 0} compatible tables` : " · estimate updates as you change party size"}</span>
                    </p>
                  )}
                  {waitQuote && waitQuote.minutes == null && (
                    <p className="text-xs mt-1 text-[#A5382A] leading-snug" data-testid="wait-quote-nofit">{waitQuote.message || "Please call to arrange a large party."}</p>
                  )}
                </div>
                <div>
                  <label className="overline block mb-1">Notes (optional)</label>
                  <input data-testid="join-notes" className="input-field" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Booth, high chair, etc." />
                </div>
                <button
                  data-testid="join-submit"
                  disabled={waitStatus?.full && !waitStatus?.standby_enabled}
                  className="btn-primary w-full justify-center disabled:opacity-50"
                >
                  {waitStatus?.full
                    ? (waitStatus?.standby_enabled ? "Join the standby list" : "Waitlist is closed")
                    : (history?.is_returning ? "Add me back to the list" : "Add me to the list")}
                </button>
              </form>
            )}
          </TabsContent>

          <TabsContent value="smartadd">
            <WaitWatchFlow slug={slug} bizName={biz.business_name} />
          </TabsContent>

          <TabsContent value="togo">
            {biz.public_features?.togo_mode === "callback" ? (
              <CallbackFlow slug={slug} bizName={biz.business_name} />
            ) : biz.public_features?.togo_mode === "external" ? (
              <div className="paper-card p-8 mt-4 text-center">
                <ShoppingBag size={40} color="#1E3A2F" className="mx-auto mb-3" />
                <h2 className="text-2xl mb-2">Order to-go on {biz.public_features?.togo_provider || "our site"}</h2>
                <p className="text-[#5C6B61] max-w-md mx-auto mb-6">We use our existing online ordering. Click below to open our to-go page in a new tab.</p>
                <a href={biz.public_features?.togo_url} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="togo-external-link">
                  Open {biz.public_features?.togo_provider || "our"} ordering →
                </a>
                <p className="text-xs text-[#5C6B61] mt-4">Your order, payments, and receipts are handled entirely by {biz.public_features?.togo_provider || "our ordering site"}.</p>
              </div>
            ) : orderPlaced ? (
              <div className="paper-card p-8 mt-4 text-center" data-testid="order-confirmed">
                <ShoppingBag size={40} color="#1E3A2F" className="mx-auto mb-2" />
                <h2 className="text-2xl mb-2">Order placed!</h2>
                <p className="text-[#5C6B61]">Total ${orderPlaced.total.toFixed(2)}. Ready in ~{orderPlaced.pickup_eta_minutes ?? 15} min ({orderPlaced.pickup_ready_at ? new Date(orderPlaced.pickup_ready_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "shortly"}). We'll text {orderPlaced.phone} when it's ready.</p>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div className="paper-card p-6">
                  <div className="overline mb-3">Menu</div>
                  {menu.length === 0 && <p className="text-sm text-[#5C6B61]">No items available.</p>}
                  <div className="space-y-3">
                    {menu.map((m) => (
                      <div key={m.id} className={`flex items-center justify-between py-2 border-b border-[#E1DFD5] last:border-0 ${m.sold_out ? "opacity-50" : ""}`}>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {m.name}
                            {m.sold_out && <span className="tag tag-cancelled text-[10px]" data-testid={`menu-item-soldout-${m.id}`}>SOLD OUT</span>}
                          </div>
                          <div className="text-xs text-[#5C6B61]">{m.description}</div>
                          <div className="text-sm mt-1">${m.price.toFixed(2)}{m.prep_minutes ? <span className="text-xs text-[#5C6B61]"> · ready ~{m.prep_minutes}m</span> : null}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button disabled={m.sold_out} onClick={() => setCart({ ...cart, [m.id]: Math.max((cart[m.id] || 0) - 1, 0) })} className="w-8 h-8 rounded-full border border-[#E1DFD5] disabled:opacity-40" data-testid={`menu-dec-${m.id}`}><Minus size={14} className="mx-auto" /></button>
                          <span className="w-6 text-center">{cart[m.id] || 0}</span>
                          <button disabled={m.sold_out} onClick={() => setCart({ ...cart, [m.id]: (cart[m.id] || 0) + 1 })} className="w-8 h-8 rounded-full bg-[#1E3A2F] text-white disabled:opacity-40" data-testid={`menu-inc-${m.id}`}><Plus size={14} className="mx-auto" color="#fff" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <form onSubmit={placeOrder} className="paper-card p-6 space-y-4">
                  <div><label className="overline block mb-1">Your name</label><input data-testid="order-name" className="input-field" required value={order.customer_name} onChange={(e) => setOrder({ ...order, customer_name: e.target.value })} /></div>
                  <div><label className="overline block mb-1">Mobile phone</label><input data-testid="order-phone" className="input-field" required value={order.phone} onChange={(e) => setOrder({ ...order, phone: e.target.value })} placeholder="+15555550100" /></div>
                  <div><label className="overline block mb-1">Notes</label><input data-testid="order-notes" className="input-field" value={order.notes} onChange={(e) => setOrder({ ...order, notes: e.target.value })} placeholder="Allergies, pickup time" /></div>
                  <div className="flex items-center justify-between border-t border-[#E1DFD5] pt-4">
                    <span className="overline">Total</span>
                    <span className="text-2xl font-heading font-extrabold">${total.toFixed(2)}</span>
                  </div>
                  <button data-testid="order-submit" className="btn-primary w-full justify-center">Place order</button>
                </form>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="mt-8">
          <GuestPrivacyCard />
        </div>
      </section>

      <ChatWidget slug={slug} businessName={biz.business_name} enabled={biz.public_features?.ai_chat} />
    </div>
  );
}
